import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useProfits } from '../hooks/useProfits';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import '../styles/profits.scss';
import { FaArrowUp, FaArrowDown, FaSyncAlt, FaInfoCircle, FaSearch } from 'react-icons/fa';

const TYPE_OPTIONS = [
    { value: 'all', label: 'الكل' },
    { value: 'balance-charge', label: 'شحن الرصيد' },
    { value: 'subscription', label: 'الاشتراكات' },
];

// Who paid: drivers (subscriptions / pay-per-trip balance) or repair-shop owners (ad balance).
const SOURCE_OPTIONS = [
    { value: 'all', label: 'الكل' },
    { value: 'driver', label: 'السائقون' },
    { value: 'shopOwner', label: 'أصحاب الورش' },
];

const FRAME_OPTIONS = [
    { value: 'day', label: 'يومي' },
    { value: 'week', label: 'أسبوعي' },
    { value: 'month', label: 'شهري' },
];

const FRAME_WORDS = {
    day: { chart: 'الأرباح اليومية', average: 'متوسط يومي', best: 'أفضل يوم' },
    week: { chart: 'الأرباح الأسبوعية', average: 'متوسط أسبوعي', best: 'أفضل أسبوع' },
    month: { chart: 'الأرباح الشهرية', average: 'متوسط شهري (30 يوم)', best: 'أفضل شهر' },
};

const TYPE_LABELS = { 'balance-charge': 'شحن الرصيد', subscription: 'الاشتراكات' };
const SERIES_COLORS = { 'balance-charge': 'var(--primary-color)', subscription: 'var(--success-color)' };

const pad = (n) => String(n).padStart(2, '0');
const toYmd = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const QUICK_RANGES = [
    { key: 'today', label: 'اليوم', range: () => { const t = new Date(); return [t, t]; } },
    { key: '7d', label: 'آخر 7 أيام', range: () => { const t = new Date(); const s = new Date(); s.setDate(t.getDate() - 6); return [s, t]; } },
    { key: '30d', label: 'آخر 30 يوم', range: () => { const t = new Date(); const s = new Date(); s.setDate(t.getDate() - 29); return [s, t]; } },
    { key: 'month', label: 'هذا الشهر', range: () => { const t = new Date(); return [new Date(t.getFullYear(), t.getMonth(), 1), t]; } },
    { key: 'lastMonth', label: 'الشهر الماضي', range: () => { const t = new Date(); return [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)]; } },
    { key: '3m', label: 'آخر 3 أشهر', range: () => { const t = new Date(); return [new Date(t.getFullYear(), t.getMonth() - 2, 1), t]; } },
    { key: 'year', label: 'هذه السنة', range: () => { const t = new Date(); return [new Date(t.getFullYear(), 0, 1), t]; } },
];

const moneyFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const formatMoney = (amount) => `${moneyFormat.format(Number(amount) || 0)} د.ل`;

// Bucket/period dates arrive as plain Libyan calendar days (YYYY-MM-DD); format them as such.
const ymdToDate = (ymd) => new Date(`${ymd}T00:00:00Z`);
const formatDay = (ymd, opts = { day: 'numeric', month: 'short' }) => ymdToDate(ymd).toLocaleDateString('ar-LY', { ...opts, timeZone: 'UTC' });
const formatBucket = (bucket, frame) => {
    if (!bucket) return '';
    if (frame === 'month') return formatDay(bucket.from, { month: 'long', year: 'numeric' });
    if (frame === 'week') return bucket.from === bucket.to ? formatDay(bucket.from) : `${formatDay(bucket.from)} - ${formatDay(bucket.to)}`;
    return formatDay(bucket.from);
};
const formatDateTime = (value) => (value ? new Date(value).toLocaleString('ar-LY', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('ar-LY', { dateStyle: 'medium' }) : '—');

const tooltipStyle = {
    backgroundColor: 'var(--side-bar-color)',
    border: '1px solid var(--borders-color)',
    borderRadius: '8px',
    direction: 'rtl',
};

export const Profits = () => {
    const user = useSelector(state => state.userController.user);
    const navigate = useNavigate();
    const { summary, isLoading, fetchSummary, debts, debtsLoading, fetchDebts } = useProfits();

    const [tab, setTab] = useState('profits');
    const [type, setType] = useState('all');
    const [source, setSource] = useState('all');
    const [frame, setFrame] = useState('day');
    const [quickRange, setQuickRange] = useState('month');
    const [dates, setDates] = useState(() => {
        const [s, e] = QUICK_RANGES.find((r) => r.key === 'month').range();
        return { startDate: toYmd(s), endDate: toYmd(e) };
    });
    const [debtSearch, setDebtSearch] = useState('');

    const load = (overrides = {}) => fetchSummary({ type, source, granularity: frame, ...dates, ...overrides });

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        load();
        fetchDebts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const applyQuickRange = (key) => {
        const [s, e] = QUICK_RANGES.find((r) => r.key === key).range();
        const next = { startDate: toYmd(s), endDate: toYmd(e) };
        setQuickRange(key);
        setDates(next);
        load(next);
    };

    const changeType = (value) => {
        setType(value);
        load({ type: value });
    };

    const changeSource = (value) => {
        setSource(value);
        load({ source: value });
    };

    const changeFrame = (value) => {
        setFrame(value);
        load({ granularity: value });
    };

    const changeDate = (field, value) => {
        setQuickRange('');
        setDates((prev) => ({ ...prev, [field]: value }));
    };

    const chartData = useMemo(
        () => (summary?.buckets || []).map((b) => ({ ...b, label: formatBucket(b, summary.granularity) })),
        [summary]
    );

    const filteredDebtors = useMemo(() => {
        const q = debtSearch.trim().toLowerCase();
        const rows = debts?.drivers || [];
        if (!q) return rows;
        return rows.filter((d) =>
            [d.driverName, d.phoneNumber, d.driverNumber].some((v) => String(v ?? '').toLowerCase().includes(q))
        );
    }, [debts, debtSearch]);

    if (!user) return null;

    const activeFrame = summary?.granularity || frame;
    const words = FRAME_WORDS[activeFrame];
    const showBothSeries = summary?.type === 'all';
    const change = summary?.percentageChange;

    return (
        <section className='container profits'>
            <div className="profits-header">
                <div>
                    <h1>الأرباح</h1>
                    <p className="muted">
                        {tab === 'profits'
                            ? 'إيرادات الشركة خلال الفترة المحددة'
                            : 'المبالغ المستحقة على السائقين بسبب الرحلات بدون اشتراك'}
                    </p>
                </div>
                <div className="tabs">
                    <button className={tab === 'profits' ? 'active' : ''} onClick={() => setTab('profits')}>
                        الأرباح
                    </button>
                    <button className={tab === 'debts' ? 'active' : ''} onClick={() => setTab('debts')}>
                        مستحقات على السائقين
                        {debts?.totalDebt > 0 && <span className="badge">{formatMoney(debts.totalDebt)}</span>}
                    </button>
                </div>
            </div>

            {tab === 'profits' ? (
                <>
                    <div className="filters-card">
                        <div className="filters-row">
                            <div className="control">
                                <span className="control-label">نوع الربح</span>
                                <div className="segmented">
                                    {TYPE_OPTIONS.map((o) => (
                                        <button key={o.value} className={type === o.value ? 'active' : ''} onClick={() => changeType(o.value)}>
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="control">
                                <span className="control-label">مصدر الربح</span>
                                <div className="segmented">
                                    {SOURCE_OPTIONS.map((o) => (
                                        <button key={o.value} className={source === o.value ? 'active' : ''} onClick={() => changeSource(o.value)}>
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="control">
                                <span className="control-label">تقسيم الرسم البياني</span>
                                <div className="segmented">
                                    {FRAME_OPTIONS.map((o) => (
                                        <button key={o.value} className={frame === o.value ? 'active' : ''} onClick={() => changeFrame(o.value)}>
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="quick-ranges">
                            {QUICK_RANGES.map((r) => (
                                <button key={r.key} className={quickRange === r.key ? 'active' : ''} onClick={() => applyQuickRange(r.key)}>
                                    {r.label}
                                </button>
                            ))}
                        </div>

                        <div className="filters-row dates">
                            <label className="control">
                                <span className="control-label">من تاريخ</span>
                                <input type="date" value={dates.startDate} max={dates.endDate} onChange={(e) => changeDate('startDate', e.target.value)} />
                            </label>
                            <label className="control">
                                <span className="control-label">إلى تاريخ</span>
                                <input type="date" value={dates.endDate} min={dates.startDate} onChange={(e) => changeDate('endDate', e.target.value)} />
                            </label>
                            <button className="primary-btn" onClick={() => load()} disabled={isLoading}>
                                <FaSyncAlt className={isLoading ? 'spin' : ''} />
                                <span>{isLoading ? 'جاري التحميل...' : 'عرض'}</span>
                            </button>
                        </div>
                    </div>

                    {!summary && isLoading && <div className="loading">جاري التحميل...</div>}

                    {summary && (
                        <div className={isLoading ? 'is-refreshing' : ''}>
                            <p className="period-caption">
                                الفترة: {formatDay(summary.period.from, { day: 'numeric', month: 'long', year: 'numeric' })}
                                {' '}—{' '}
                                {formatDay(summary.period.to, { day: 'numeric', month: 'long', year: 'numeric' })}
                                {' '}({summary.period.days} يوم)
                            </p>

                            <div className="kpi-grid">
                                <div className="kpi-card primary">
                                    <h3>إجمالي الأرباح</h3>
                                    <p className="kpi-value">{formatMoney(summary.total)}</p>
                                    <p className="kpi-sub">{summary.count} عملية</p>
                                </div>

                                <div className="kpi-card info">
                                    <h3>متوسط يومي</h3>
                                    <p className="kpi-value">{formatMoney(summary.averagePerDay)}</p>
                                    <p className="kpi-sub">الإجمالي ÷ {summary.period.days} يوم</p>
                                </div>

                                {activeFrame !== 'day' && (
                                    <div className="kpi-card info">
                                        <h3>{words.average}</h3>
                                        <p className="kpi-value">{formatMoney(summary.averagePerFrame)}</p>
                                        <p className="kpi-sub">المتوسط اليومي × {activeFrame === 'week' ? 7 : 30}</p>
                                    </div>
                                )}

                                <div className={`kpi-card ${change === null ? '' : change >= 0 ? 'positive' : 'negative'}`}>
                                    <h3>مقارنة بالفترة السابقة</h3>
                                    <p className="kpi-value">
                                        {change === null ? '—' : (
                                            <>
                                                {change >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                                                {Math.abs(change).toFixed(1)}%
                                            </>
                                        )}
                                    </p>
                                    <p className="kpi-sub">
                                        {change === null
                                            ? 'لا توجد أرباح في الفترة السابقة'
                                            : `الفترة السابقة: ${formatMoney(summary.previousTotal)}`}
                                    </p>
                                </div>

                                {showBothSeries && ['balance-charge', 'subscription'].map((t) => (
                                    <div key={t} className="kpi-card" style={{ borderRightColor: SERIES_COLORS[t] }}>
                                        <h3>{TYPE_LABELS[t]}</h3>
                                        <p className="kpi-value">{formatMoney(summary.byType[t].total)}</p>
                                        <p className="kpi-sub">
                                            {summary.byType[t].count} عملية
                                            {summary.total > 0 && ` · ${((summary.byType[t].total / summary.total) * 100).toFixed(0)}% من الإجمالي`}
                                        </p>
                                    </div>
                                ))}

                                <div className="kpi-card">
                                    <h3>{words.best}</h3>
                                    <p className="kpi-value">{summary.bestBucket ? formatMoney(summary.bestBucket.total) : '—'}</p>
                                    <p className="kpi-sub">{summary.bestBucket ? formatBucket(summary.bestBucket, activeFrame) : 'لا توجد أرباح'}</p>
                                </div>
                            </div>

                            <div className="chart-card">
                                <div className="chart-card__header">
                                    <h2>{words.chart}</h2>
                                    <span className="muted">الخط المتقطع = {words.average}: {formatMoney(activeFrame === 'day' ? summary.averagePerDay : summary.averagePerFrame)}</span>
                                </div>
                                <ResponsiveContainer width="100%" height={340}>
                                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--borders-color)" vertical={false} />
                                        <XAxis dataKey="label" stroke="var(--normal-text-color)" tick={{ fontSize: 12 }} interval="preserveStartEnd" minTickGap={12} />
                                        <YAxis stroke="var(--normal-text-color)" tick={{ fontSize: 12 }} tickFormatter={(v) => moneyFormat.format(v)} />
                                        <Tooltip
                                            formatter={(value, name) => [formatMoney(value), name]}
                                            labelFormatter={(label, payload) => {
                                                const b = payload?.[0]?.payload;
                                                return b ? `${formatBucket(b, activeFrame)} — الإجمالي ${formatMoney(b.total)} (${b.count} عملية)` : label;
                                            }}
                                            contentStyle={tooltipStyle}
                                            cursor={{ fill: 'var(--borders-color)', opacity: 0.4 }}
                                        />
                                        <Legend />
                                        {showBothSeries ? (
                                            <>
                                                <Bar dataKey="balance-charge" stackId="profit" fill={SERIES_COLORS['balance-charge']} name={TYPE_LABELS['balance-charge']} maxBarSize={48} />
                                                <Bar dataKey="subscription" stackId="profit" fill={SERIES_COLORS.subscription} name={TYPE_LABELS.subscription} radius={[6, 6, 0, 0]} maxBarSize={48} />
                                            </>
                                        ) : (
                                            <Bar dataKey="total" fill={SERIES_COLORS[summary.type]} name={TYPE_LABELS[summary.type]} radius={[6, 6, 0, 0]} maxBarSize={48} />
                                        )}
                                        <ReferenceLine
                                            y={activeFrame === 'day' ? summary.averagePerDay : summary.averagePerFrame}
                                            stroke="var(--warning-color)"
                                            strokeDasharray="6 4"
                                            strokeWidth={2}
                                            ifOverflow="extendDomain"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="breakdown-grid">
                                {summary.type !== 'subscription' && (
                                    <div className="chart-card">
                                        <h2>شحن الرصيد حسب الفئة</h2>
                                        {summary.distribution.vouchers.length ? (
                                            <ul className="breakdown-list">
                                                {summary.distribution.vouchers.map((v) => (
                                                    <li key={v.name}>
                                                        <span className="name">{v.name}</span>
                                                        <span className="count">{v.count} ×</span>
                                                        <span className="amount">{formatMoney(v.totalAmount)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : <p className="muted">لا توجد عمليات شحن في هذه الفترة</p>}
                                    </div>
                                )}
                                {summary.type !== 'balance-charge' && (
                                    <div className="chart-card">
                                        <h2>الاشتراكات حسب الباقة</h2>
                                        {summary.distribution.packages.length ? (
                                            <ul className="breakdown-list">
                                                {summary.distribution.packages.map((p) => (
                                                    <li key={p.name}>
                                                        <span className="name">{p.name}</span>
                                                        <span className="count">{p.count} ×</span>
                                                        <span className="amount">{formatMoney(p.totalAmount)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : <p className="muted">لا توجد اشتراكات محتسبة في هذه الفترة</p>}
                                    </div>
                                )}
                            </div>

                            <div className="chart-card">
                                <div className="chart-card__header">
                                    <h2>العمليات</h2>
                                    <span className="muted">
                                        {summary.transactionsTruncated ? `آخر ${summary.transactions.length} عملية من ${summary.count}` : `${summary.count} عملية`}
                                    </span>
                                </div>
                                {summary.transactions.length ? (
                                    <div className="table-wrap">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>التاريخ</th>
                                                    <th>النوع</th>
                                                    <th>التفاصيل</th>
                                                    <th>المبلغ</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {summary.transactions.map((t) => (
                                                    <tr key={t._id}>
                                                        <td>{formatDateTime(t.createdAt)}</td>
                                                        <td><span className={`type-chip ${t.type}`}>{TYPE_LABELS[t.type] || t.type}</span></td>
                                                        <td>{t.name || '—'}</td>
                                                        <td className="amount">{formatMoney(t.amount)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : <p className="muted">لا توجد عمليات في هذه الفترة</p>}
                            </div>

                            <p className="info-note">
                                <FaInfoCircle />
                                <span>
                                    تُحتسب الأرباح عند دخول المال فعلياً: شحن الرصيد (قسائم الشحن، أو الشحن النقدي من الإدارة عند اختيار احتسابه)،
                                    والاشتراكات التي تنشئها الإدارة مع خيار "احتساب ضمن الأرباح". الاشتراكات التي يشتريها السائق من رصيده لا تُحتسب مرة أخرى،
                                    لأن قيمتها احتُسبت عند شحن الرصيد.
                                </span>
                            </p>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className="kpi-grid">
                        <div className="kpi-card negative">
                            <h3>إجمالي المستحقات</h3>
                            <p className="kpi-value">{debts ? formatMoney(debts.totalDebt) : '—'}</p>
                            <p className="kpi-sub">مجموع الأرصدة السالبة</p>
                        </div>
                        <div className="kpi-card">
                            <h3>عدد السائقين المدينين</h3>
                            <p className="kpi-value">{debts?.driversCount ?? '—'}</p>
                            <p className="kpi-sub">رصيدهم أقل من صفر</p>
                        </div>
                        <div className="kpi-card warning">
                            <h3>تجاوزوا الحد المسموح</h3>
                            <p className="kpi-value">{debts?.exceededCount ?? '—'}</p>
                            <p className="kpi-sub">{debts?.maxDebit ? `رصيدهم لا يكفي لرحلة أخرى ضمن حد ${formatMoney(debts.maxDebit)} — لا يستقبلون طلبات` : 'لا يوجد حد محدد'}</p>
                        </div>
                    </div>

                    {debts && !debts.payPerTripEnabled && (
                        <p className="info-note">
                            <FaInfoCircle />
                            <span>خاصية الدفع لكل رحلة متوقفة حالياً، لذلك لن تتراكم مستحقات جديدة.</span>
                        </p>
                    )}

                    <div className="chart-card">
                        <div className="chart-card__header">
                            <h2>السائقون المدينون</h2>
                            <div className="table-tools">
                                <div className="search-input">
                                    <FaSearch />
                                    <input
                                        type="text"
                                        placeholder="بحث بالاسم أو الهاتف أو رقم السائق"
                                        value={debtSearch}
                                        onChange={(e) => setDebtSearch(e.target.value)}
                                    />
                                </div>
                                <button className="ghost-btn" onClick={fetchDebts} disabled={debtsLoading}>
                                    <FaSyncAlt className={debtsLoading ? 'spin' : ''} />
                                    <span>تحديث</span>
                                </button>
                            </div>
                        </div>

                        {debtsLoading && !debts ? (
                            <div className="loading">جاري التحميل...</div>
                        ) : filteredDebtors.length ? (
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>السائق</th>
                                            <th>رقم الهاتف</th>
                                            <th>المبلغ المستحق</th>
                                            <th>آخر رحلة بالخصم</th>
                                            <th>رحلات مخصومة</th>
                                            <th>الحالة</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredDebtors.map((d) => (
                                            <tr key={d._id}>
                                                <td>
                                                    <div className="driver-cell">
                                                        <span className="name">{d.driverName}</span>
                                                        <span className="muted">
                                                            {d.driverNumber ? `#${d.driverNumber}` : ''}
                                                            {d.city ? ` · ${d.city}${d.area ? ` - ${d.area}` : ''}` : ''}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td dir="ltr" className="phone">{d.phoneNumber}</td>
                                                <td className="amount debt">{formatMoney(d.debt)}</td>
                                                <td>
                                                    {d.lastDebitTripAt ? formatDate(d.lastDebitTripAt) : (
                                                        <span className="muted" title="رحلات قديمة قبل تسجيل رسوم كل رحلة">
                                                            {d.lastTripAt ? `آخر رحلة: ${formatDate(d.lastTripAt)}` : '—'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{d.debitTripsCount}</td>
                                                <td>
                                                    <div className="chips">
                                                        {d.exceededLimit && <span className="status-chip danger">تجاوز الحد</span>}
                                                        {d.state === 'deleted' && <span className="status-chip muted">محذوف</span>}
                                                        {d.isBanned && <span className="status-chip danger">محظور</span>}
                                                        {!d.exceededLimit && d.state !== 'deleted' && !d.isBanned && <span className="status-chip warning">مدين</span>}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="muted">{debts?.driversCount ? 'لا توجد نتائج مطابقة للبحث' : 'لا يوجد أي سائق عليه مستحقات حالياً'}</p>
                        )}
                    </div>
                </>
            )}
        </section>
    );
};

export default Profits;
